import { PublicRoutes } from "@/constants/routes";
import {
  BadgeCheck,
  Cookie,
  MessageCircle,
  ShieldCheck,
  Store,
  UserRound,
} from "lucide-react";
import { ACCOUNT_TERMS, COMPANY, DPO } from "./company";

const { readOnlyDays, retentionDays } = ACCOUNT_TERMS;
import type { LegalDocument } from "./types";

export const PRIVACY_POLICY: LegalDocument = {
  key: "privacidade",
  path: PublicRoutes.POLITICA_PRIVACIDADE,
  eyebrow: "Privacidade",
  title: "Política de Privacidade",
  seoTitle: "Política de Privacidade",
  description:
    "Como a Fidexa trata dados de lojistas, visitantes e clientes das lojas: o que coletamos, para quê, com quem compartilhamos e como exercer seus direitos na LGPD.",
  intro:
    "Aqui explicamos, sem juridiquês, quais dados pessoais passam pela Fidexa, por que eles passam e o que você pode pedir sobre eles. Cada seção abre com um resumo; o texto completo vem logo depois.",
  version: "2.0",
  updatedAt: "2026-10-12",

  highlights: [
    {
      icon: ShieldCheck,
      title: "Não vendemos dados",
      text: "Não vendemos, alugamos nem trocamos dados pessoais de ninguém: nem de lojistas, nem dos clientes das lojas.",
    },
    {
      icon: Store,
      title: "A loja decide sobre os clientes dela",
      text: "Os dados dos clientes do crediário pertencem à loja. A Fidexa trata esses dados só para executar o serviço que a loja contratou.",
    },
    {
      icon: UserRound,
      title: "Seus direitos, com prazo",
      text: "Você pode pedir acesso, correção, exclusão e mais. Respondemos em até 15 dias.",
    },
    {
      icon: Cookie,
      title: "Cookies de marketing só com aceite",
      text: "Cookies de análise e de anúncios só são ativados se você aceitar no aviso de cookies.",
    },
  ],

  audiencePaths: [
    {
      audience: "lojista",
      icon: Store,
      label: "Sou lojista ou uso a plataforma",
      description:
        "Entenda o papel da sua loja e o da Fidexa sobre os dados dos seus clientes.",
      sectionId: "dois-papeis",
    },
    {
      audience: "cliente-da-loja",
      icon: MessageCircle,
      label: "Recebi uma mensagem de uma loja",
      description:
        "Saiba por que a loja falou com você pela Fidexa e como parar de receber.",
      sectionId: "recebi-uma-mensagem",
    },
    {
      audience: "visitante",
      icon: BadgeCheck,
      label: "Só visitei o site",
      description:
        "Veja quais cookies usamos e como recusar os que não são essenciais.",
      sectionId: "cookies",
    },
  ],

  sections: [
    {
      id: "quem-somos",
      title: "Quem somos e o que esta política cobre",
      summary:
        "A Fidexa é uma plataforma de gestão e cobrança de crediário para o varejo. Esta política vale para o site fidexa.com.br, a plataforma e os canais de atendimento.",
      blocks: [
        {
          type: "paragraph",
          text: `A **${COMPANY.legalName}**, inscrita no CNPJ sob o nº ${COMPANY.cnpj}, com sede na ${COMPANY.address} ("Fidexa"), oferece às lojas uma plataforma, no modelo de software como serviço, para organizar o crediário próprio e se comunicar com os clientes pelo WhatsApp.`,
        },
        {
          type: "paragraph",
          text: "Esta política se aplica ao site fidexa.com.br e às suas páginas, à plataforma usada pelas lojas, aos formulários de contato e de demonstração e aos nossos canais de suporte. Ela segue a Lei Geral de Proteção de Dados Pessoais (Lei nº 13.709/2018, a **LGPD**), o Marco Civil da Internet (Lei nº 12.965/2014) e o Código de Defesa do Consumidor (Lei nº 8.078/1990).",
        },
        {
          type: "paragraph",
          text: `Para entender como cumprimos a LGPD no dia a dia, veja também a página [LGPD na Fidexa](${PublicRoutes.LGPD}).`,
        },
      ],
    },
    {
      id: "dois-papeis",
      title: "Dois papéis: quando a loja decide e quando a Fidexa decide",
      summary:
        "Sobre os dados dos clientes do crediário, quem decide é a loja (controladora) e a Fidexa executa (operadora). Sobre os dados de quem usa a plataforma e visita o site, quem decide é a Fidexa.",
      blocks: [
        {
          type: "paragraph",
          text: "A LGPD separa quem **decide** sobre o uso dos dados (o controlador) de quem **trata os dados em nome dele** (o operador). Na Fidexa, os dois papéis existem, e é importante saber qual vale para cada caso.",
        },
        {
          type: "table",
          caption: "Qual é o papel da Fidexa em cada caso",
          columns: [
            { key: "dados", header: "Dados de quem", primary: true },
            { key: "controlador", header: "Quem decide (controlador)" },
            { key: "fidexa", header: "Papel da Fidexa" },
          ],
          rows: [
            {
              dados: "Clientes do crediário da loja",
              controlador: "A loja que contratou a Fidexa",
              fidexa:
                "**Operadora**: trata os dados só para prestar o serviço contratado, conforme as instruções da loja.",
            },
            {
              dados: "Lojistas e usuários da plataforma",
              controlador: "A Fidexa",
              fidexa:
                "**Controladora**: decide como os dados são usados para manter a conta, faturar e dar suporte.",
            },
            {
              dados: "Visitantes do site e interessados (leads)",
              controlador: "A Fidexa",
              fidexa:
                "**Controladora**: decide como os dados são usados para responder contatos e medir o site.",
            },
          ],
        },
        {
          type: "paragraph",
          text: "Como controladora dos dados dos próprios clientes, a loja é responsável por ter base legal para tratá-los e por fazer a cobrança de forma lícita, sem expor o cliente a ridículo, constrangimento ou ameaça (Código de Defesa do Consumidor, arts. 42 e 71). A Fidexa não define o conteúdo, o tom nem a frequência das mensagens que a loja envia.",
        },
      ],
    },
    {
      id: "dados-que-coletamos",
      title: "Dados que coletamos de você",
      summary:
        "Coletamos o necessário para criar sua conta, prestar o serviço, responder seu contato e manter o site seguro. Nada além disso.",
      blocks: [
        { type: "subheading", text: "Se você é lojista ou usa a plataforma" },
        {
          type: "list",
          items: [
            "**Cadastro:** nome, sobrenome, telefone, e-mail, empresa, CNPJ e as lojas a que você tem acesso.",
            "**Acesso:** credenciais de login, permissões e registros de acesso (endereço IP, data e hora).",
            "**Uso da plataforma:** ações realizadas, como importações, envios e atendimentos, para registro e suporte.",
            "**Financeiro:** dados de faturamento da empresa contratante e o contato responsável pelo pagamento.",
            "**Atendimento:** o que você nos conta no suporte, por e-mail ou WhatsApp.",
          ],
        },
        { type: "subheading", text: "Se você pediu uma demonstração" },
        {
          type: "list",
          items: [
            "Nome, nome da rede, WhatsApp, e-mail e número de lojas, informados no formulário.",
          ],
        },
        { type: "subheading", text: "Se você visitou o site" },
        {
          type: "list",
          items: [
            "Dados de navegação, como páginas visitadas, origem da visita, tipo de dispositivo e navegador, coletados por cookies e tecnologias parecidas. Os detalhes estão na seção [Cookies](#cookies).",
          ],
        },
      ],
    },
    {
      id: "dados-dos-clientes-das-lojas",
      title: "Dados que tratamos em nome das lojas",
      summary:
        "Para a loja organizar o crediário e falar com os clientes, a plataforma guarda dados de identificação, contato e parcelas. Esses dados são da loja e usados só para o serviço dela.",
      blocks: [
        {
          type: "paragraph",
          text: "A loja envia esses dados à Fidexa pela integração com o sistema de gestão (ERP) ou o gateway de pagamento que ela usa, por planilha ou por cadastro manual. Conforme o que a loja usa, podem entrar:",
        },
        {
          type: "list",
          items: [
            "**Identificação:** nome, CPF e data de nascimento.",
            "**Contato:** telefone e WhatsApp, e-mail e endereço.",
            "**Crediário:** compras parceladas, parcelas, valores, vencimentos, pagamentos, acordos e limite de crédito.",
            "**Relacionamento:** mensagens trocadas pelo WhatsApp, avaliações de atendimento e documentos que a loja anexar ao cadastro.",
          ],
        },
        {
          type: "paragraph",
          text: `Sobre esses dados, a Fidexa se compromete a: tratá-los **só conforme as instruções da loja** e para prestar o serviço; manter sigilo; adotar medidas de segurança; avisar a loja sobre incidente de segurança que possa trazer risco ou dano relevante; e, ao fim do contrato, eliminá-los nos prazos da [Política de Cancelamento](${PublicRoutes.POLITICA_CANCELAMENTO}#depois-do-cancelamento), exceto o que a lei mandar guardar.`,
        },
        {
          type: "paragraph",
          text: "A Fidexa pode gerar **estatísticas agregadas e anonimizadas**, como o valor total recuperado pela plataforma. Dado anonimizado não identifica ninguém e, pela LGPD (art. 12), deixa de ser dado pessoal.",
        },
      ],
    },
    {
      id: "finalidades-e-bases-legais",
      title: "Para que usamos os dados e com que base legal",
      summary:
        "Todo uso de dado tem uma finalidade e uma base legal da LGPD. Consentimento só é pedido para cookies de análise e de anúncios.",
      blocks: [
        {
          type: "paragraph",
          text: "Esta tabela vale para os dados em que a Fidexa é controladora. Sobre os dados dos clientes das lojas, a base legal é definida pela loja.",
        },
        {
          type: "table",
          caption: "Finalidades e bases legais (LGPD, art. 7º)",
          columns: [
            { key: "finalidade", header: "Finalidade", primary: true },
            { key: "dados", header: "Dados usados" },
            { key: "base", header: "Base legal" },
          ],
          rows: [
            {
              finalidade: "Criar e manter sua conta e prestar o serviço",
              dados: "Cadastro, acesso e uso da plataforma",
              base: "Execução de contrato (art. 7º, V)",
            },
            {
              finalidade: "Faturar, cobrar a mensalidade e emitir nota fiscal",
              dados: "Dados da empresa e contato financeiro",
              base: "Execução de contrato e obrigação legal (art. 7º, V e II)",
            },
            {
              finalidade: "Dar suporte e avisar sobre mudanças no serviço",
              dados: "Contato e histórico de atendimento",
              base: "Execução de contrato (art. 7º, V)",
            },
            {
              finalidade:
                "Responder o pedido de demonstração e enviar conteúdo sobre a Fidexa",
              dados: "Dados do formulário",
              base: "Legítimo interesse (art. 7º, IX), com opção de sair a qualquer momento",
            },
            {
              finalidade:
                "Guardar registros de acesso, prevenir fraude e proteger a plataforma",
              dados: "IP, data, hora e ações na conta",
              base: "Obrigação legal (Marco Civil, art. 15) e legítimo interesse (art. 7º, II e IX)",
            },
            {
              finalidade: "Medir a audiência do site e melhorar a navegação",
              dados: "Cookies de análise",
              base: "Consentimento (art. 7º, I)",
            },
            {
              finalidade: "Medir e direcionar campanhas de anúncios",
              dados: "Cookies de marketing",
              base: "Consentimento (art. 7º, I)",
            },
            {
              finalidade:
                "Defender a Fidexa em processos judiciais, administrativos ou arbitrais",
              dados: "Registros e documentos relacionados",
              base: "Exercício regular de direitos (art. 7º, VI)",
            },
          ],
        },
        {
          type: "paragraph",
          text: "Quando usamos o legítimo interesse, avaliamos antes se o uso é esperado por você e se não passa por cima dos seus direitos. Você pode se opor a esse uso a qualquer momento pelo canal do encarregado.",
        },
      ],
    },
    {
      id: "compartilhamento",
      title: "Com quem compartilhamos",
      summary:
        "Só com fornecedores que fazem o serviço funcionar, com contrato e dever de sigilo, e com autoridades quando a lei obriga.",
      blocks: [
        {
          type: "table",
          caption: "Fornecedores que recebem dados pessoais",
          columns: [
            { key: "quem", header: "Fornecedor", primary: true },
            { key: "para", header: "Para quê" },
          ],
          rows: [
            {
              quem: "Meta Platforms (WhatsApp Business Platform)",
              para: "Entregar as mensagens que a loja envia aos clientes pelo WhatsApp.",
            },
            {
              quem: "Hostinger",
              para: "Hospedar os servidores e o banco de dados da plataforma.",
            },
            {
              quem: "Vercel",
              para: "Hospedar o site fidexa.com.br.",
            },
            {
              quem: "Microsoft Azure",
              para: "Guardar as cópias de segurança (backups).",
            },
            {
              quem: "Resend",
              para: "Enviar e-mails do serviço, como avisos e confirmações.",
            },
            {
              quem: "Google (Tag Manager e Analytics)",
              para: "Medir a audiência do site, só com o seu consentimento.",
            },
            {
              quem: "Meta (Pixel)",
              para: "Medir campanhas de anúncios, só com o seu consentimento.",
            },
            {
              quem: "PostHog",
              para: "Entender como o site e a plataforma são usados, para melhorá-los.",
            },
          ],
        },
        {
          type: "paragraph",
          text: "Quando a loja conecta a Fidexa ao ERP ou ao gateway de pagamento dela, os dados vêm desses sistemas por escolha e sob responsabilidade da loja. Esses sistemas são contratados pela loja, não pela Fidexa.",
        },
        {
          type: "paragraph",
          text: "Também podemos compartilhar dados com autoridades públicas e com o Poder Judiciário quando houver obrigação legal ou ordem judicial, e em uma eventual operação societária (fusão, aquisição ou incorporação), em que o novo controlador assume os mesmos compromissos desta política.",
        },
      ],
    },
    {
      id: "transferencia-internacional",
      title: "Transferência internacional de dados",
      summary:
        "Alguns fornecedores, como Meta, Google e Microsoft, podem processar dados fora do Brasil. Nesses casos, seguimos as regras da LGPD para transferência internacional.",
      blocks: [
        {
          type: "paragraph",
          text: "Parte dos fornecedores da seção anterior mantém servidores em outros países, como os Estados Unidos e países da União Europeia. Essas transferências se apoiam nos mecanismos do art. 33 da LGPD e da Resolução CD/ANPD nº 19/2024, como cláusulas contratuais de proteção de dados, e nos compromissos de segurança assumidos por esses fornecedores.",
        },
      ],
    },
    {
      id: "por-quanto-tempo",
      title: "Por quanto tempo guardamos",
      summary: `Pelo tempo do contrato e, depois, só pelo necessário. Ao fim do acesso, a loja tem ${readOnlyDays} dias para consultar os dados e pedir a cópia; eles ficam bloqueados até completar ${retentionDays} dias e então são eliminados.`,
      blocks: [
        {
          type: "paragraph",
          text: "Guardamos cada dado pelo tempo necessário para a finalidade que justificou a coleta. Alguns ficam por mais tempo por exigência legal, por exemplo os registros de acesso (6 meses, pelo Marco Civil da Internet) e os documentos fiscais (5 anos).",
        },
        {
          type: "paragraph",
          text: `Quando o acesso termina, a plataforma fica disponível só para leitura por **${readOnlyDays} dias**, e a loja pode pedir uma cópia dos dados por e-mail. Depois, os dados ficam guardados e bloqueados, sem acesso nem uso para outra finalidade, até completar **${retentionDays} dias**, para comprovar o que foi feito em caso de reclamação ou disputa (LGPD, art. 7º, VI). Então são eliminados, exceto o que a lei mandar guardar por mais tempo. Veja a [Política de Cancelamento](${PublicRoutes.POLITICA_CANCELAMENTO}).`,
        },
        {
          type: "paragraph",
          text: `Os prazos de cada tipo de dado estão na página [Retenção de dados](${PublicRoutes.RETENCAO_DADOS}).`,
        },
      ],
    },
    {
      id: "seus-direitos",
      title: "Seus direitos e como exercê-los",
      summary:
        "Você pode pedir confirmação, acesso, correção, exclusão, portabilidade e mais. Basta escrever para o encarregado; respondemos em até 15 dias.",
      blocks: [
        {
          type: "paragraph",
          text: "A LGPD (art. 18) garante a você, titular dos dados, o direito de pedir:",
        },
        {
          type: "list",
          items: [
            "**Confirmação** de que tratamos dados seus e **acesso** a eles.",
            "**Correção** de dados incompletos, inexatos ou desatualizados.",
            "**Anonimização, bloqueio ou eliminação** de dados desnecessários, excessivos ou tratados em desacordo com a lei.",
            "**Portabilidade** dos dados a outro fornecedor, conforme a regulamentação da ANPD.",
            "**Eliminação** dos dados tratados com seu consentimento.",
            "**Informação** sobre com quem compartilhamos seus dados.",
            "**Informação** sobre a possibilidade de não dar consentimento e o que acontece se você negar.",
            "**Revogação** do consentimento, a qualquer momento.",
            "**Oposição** a um tratamento feito sem consentimento, se ele descumprir a LGPD.",
            "**Revisão** de decisões tomadas só por tratamento automatizado que afetem seus interesses (art. 20).",
          ],
        },
        {
          type: "paragraph",
          text: `Para fazer um pedido, escreva para [${DPO.email}](mailto:${DPO.email}). Podemos pedir uma confirmação de identidade antes de responder, para proteger você. A resposta completa sai em até **15 dias** (art. 19).`,
        },
        {
          type: "paragraph",
          text: "Se o pedido for sobre dados que uma loja cadastrou, a decisão é dela, que é a controladora. Nesse caso, encaminhamos seu pedido à loja, avisamos você e apoiamos a loja na resposta.",
        },
        {
          type: "paragraph",
          text: "Se achar que seu pedido não foi atendido, você pode reclamar à Autoridade Nacional de Proteção de Dados ([ANPD](https://www.gov.br/anpd)).",
        },
      ],
    },
    {
      id: "recebi-uma-mensagem",
      title: "Recebi uma mensagem de uma loja pela Fidexa",
      summary:
        "Você é cliente de uma loja que usa a Fidexa para falar sobre o seu crediário. Quem tem seus dados é a loja; para não receber mais mensagens, é só pedir à loja ou a nós.",
      blocks: [
        {
          type: "paragraph",
          text: "Se você comprou no crediário de uma loja que usa a Fidexa, pode receber dela, pelo WhatsApp, lembretes de vencimento, avisos de parcelas em aberto e propostas de acordo. A loja é quem tem seus dados, porque você é cliente dela; a Fidexa é a ferramenta que a loja usa para enviar as mensagens.",
        },
        {
          type: "list",
          items: [
            "**Não quer mais receber mensagens?** Responda a própria conversa pedindo para parar ou fale com a loja. Se preferir, escreva para o nosso encarregado: encaminhamos o pedido à loja.",
            "**Dados errados ou mensagem que não é para você?** Avise a loja ou o nosso encarregado para corrigir o cadastro.",
            "**Quer saber que dados a loja tem?** O pedido é feito à loja. Se você escrever para nós, encaminhamos.",
          ],
        },
        {
          type: "callout",
          tone: "warning",
          title: "Cuidado com golpes",
          text: `A Fidexa e as lojas nunca pedem senha, código de verificação ou dados de cartão pelo WhatsApp. Antes de pagar um Pix, confira se o nome do recebedor é o da loja em que você comprou. Na dúvida, ligue para a loja. Veja mais em [Segurança](${PublicRoutes.SEGURANCA}).`,
        },
      ],
    },
    {
      id: "seguranca",
      title: "Como protegemos os dados",
      summary:
        "Usamos medidas técnicas e administrativas para proteger os dados contra acesso indevido, perda ou vazamento. Se houver um incidente relevante, avisamos quem precisa saber.",
      blocks: [
        {
          type: "paragraph",
          text: "Adotamos medidas de segurança técnicas e administrativas aptas a proteger os dados pessoais de acessos não autorizados e de situações acidentais ou ilícitas de destruição, perda, alteração, comunicação ou difusão (LGPD, art. 46). Entre elas, conexão criptografada, controle de acesso por perfil de usuário e cópias de segurança guardadas em um provedor separado dos servidores.",
        },
        {
          type: "paragraph",
          text: "Se ocorrer um incidente de segurança que possa trazer risco ou dano relevante, avisamos as lojas afetadas e, quando formos controladores, a ANPD e os titulares, nos prazos da Resolução CD/ANPD nº 15/2024.",
        },
        {
          type: "paragraph",
          text: `Os detalhes estão na página [Segurança](${PublicRoutes.SEGURANCA}).`,
        },
      ],
    },
    {
      id: "cookies",
      title: "Cookies",
      summary:
        "Usamos cookies essenciais para o site funcionar e, só se você aceitar, cookies de análise e de anúncios. Você pode recusar sem perder nada do site.",
      blocks: [
        {
          type: "paragraph",
          text: "Cookies são pequenos arquivos guardados no navegador. Na primeira visita, mostramos um aviso com os botões **Aceitar** e **Recusar**, com o mesmo destaque. Sua escolha fica salva no navegador.",
        },
        {
          type: "table",
          caption: "Tipos de cookies que usamos",
          columns: [
            { key: "tipo", header: "Tipo", primary: true },
            { key: "para", header: "Para quê" },
            { key: "quando", header: "Quando é ativado" },
          ],
          rows: [
            {
              tipo: "Essenciais",
              para: "Manter você conectado na plataforma, lembrar sua escolha de cookies e proteger o site.",
              quando: "Sempre: sem eles o site não funciona.",
            },
            {
              tipo: "Análise",
              para: "Contar visitas e entender quais páginas ajudam mais (Google Analytics, PostHog e a medição do blog).",
              quando: "Só se você aceitar.",
            },
            {
              tipo: "Marketing",
              para: "Medir o resultado de anúncios (Google e Meta Pixel).",
              quando: "Só se você aceitar.",
            },
          ],
        },
        {
          type: "paragraph",
          text: "Na plataforma, depois do login, registramos como ela é usada (por exemplo, as etapas do acesso e as telas visitadas) para dar suporte e melhorar o serviço, com base na execução do contrato e no legítimo interesse. Sem o seu aceite, esse registro não grava cookies nem dados de análise no seu navegador.",
        },
        {
          type: "paragraph",
          text: "Para mudar de ideia, apague os dados do site fidexa.com.br no seu navegador: o aviso aparece de novo na próxima visita. Você também pode bloquear cookies nas configurações do navegador.",
        },
      ],
    },
    {
      id: "criancas-e-adolescentes",
      title: "Crianças e adolescentes",
      summary:
        "O site e a plataforma são para empresas e não se destinam a menores de 18 anos.",
      blocks: [
        {
          type: "paragraph",
          text: "Não coletamos de propósito dados de crianças e adolescentes. Se uma loja cadastrar dados de um adolescente, ela, como controladora, deve observar o melhor interesse dele (LGPD, art. 14). Se você souber de um caso assim, fale com o encarregado.",
        },
      ],
    },
    {
      id: "mudancas",
      title: "Mudanças nesta política",
      summary:
        "Quando a política mudar, a nova versão aparece aqui com data e histórico. Mudanças relevantes são avisadas às lojas com antecedência.",
      blocks: [
        {
          type: "paragraph",
          text: "Podemos atualizar esta política para refletir mudanças no serviço ou na lei. A versão em vigor é sempre a desta página, com a data de atualização no topo e o histórico de alterações no fim. Mudanças relevantes são comunicadas às lojas por e-mail ou pela plataforma, com pelo menos 30 dias de antecedência.",
        },
      ],
    },
  ],

  changelog: [
    {
      version: "2.0",
      date: "2026-10-12",
      changes: [
        "Política reescrita em linguagem simples, com resumo em cada seção.",
        "Separação entre os papéis de controladora e operadora.",
        "Inclusão das bases legais, dos fornecedores, da transferência internacional e dos prazos de retenção.",
        "Nova seção para quem recebe mensagens das lojas.",
        "Indicação do encarregado de dados.",
      ],
    },
    {
      version: "1.0",
      date: "2025-04-24",
      changes: ["Primeira versão publicada."],
    },
  ],
};
